import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaCheck, FaExternalLinkAlt } from 'react-icons/fa';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { fetchToken } from '../../../lib/globalApiClient';
import noImage from '../../../assets/images/common/importerr-no-image-logo.png';
import './productVariants.css';

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

export default function ProductVariants() {
  const location = useLocation();
  const navigate = useNavigate();
  const message = useMessage();

  const searchParams = new URLSearchParams(location.search);
  const offerId = searchParams.get('offerId') || '';
  const asin = searchParams.get('asin') || '';
  const title = decodeURIComponent(searchParams.get('title') || '');
  const image = decodeURIComponent(searchParams.get('image') || '');
  const rmb = parseFloat(searchParams.get('rmb') || 0);
  const inr = parseFloat(searchParams.get('inr') || 0);

  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [productDetails, setProductDetails] = useState(null);
  const [mappingSkuId, setMappingSkuId] = useState(null);
  const [pricingCache, setPricingCache] = useState({});
  const [apiError, setApiError] = useState(null);
  const [rawResponse, setRawResponse] = useState(null);

  // Calculate INR price
  const calculateINRPrice = useCallback(async (rmbPrice) => {
    if (!rmbPrice || rmbPrice <= 0) return 0;
    const cacheKey = rmbPrice.toFixed(2);
    if (pricingCache[cacheKey]) return pricingCache[cacheKey];

    try {
      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ap: parseFloat(rmbPrice), ls: 0, hsn: false, hsnTaxPercent: 18, dw: 0.5, vw: 0.5 / 5000, sku: offerId })
      });
      if (!response.ok) return 0;
      const result = await response.json();
      const inrPrice = parseFloat(result?.data?.breakdown?.FBP || result?.breakdown?.FBP || 0);
      setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
      return inrPrice;
    } catch { return 0; }
  }, [pricingCache, offerId]);

  // Fetch product variants from 1688
  useEffect(() => {
    const fetchVariants = async () => {
      if (!offerId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const token = await fetchToken();
        // Call the product detail API - using the correct endpoint
        const response = await fetch(`${process.env.REACT_APP_APIUrl1688}/product-detail-by-offerid`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Token': token
          },
          body: JSON.stringify({
            offerId: offerId.toString(),
            country: 'en'
          })
        });

        if (!response.ok) throw new Error('Failed to fetch product details');

        const data = await response.json();
        console.log('Product detail API response:', data);
        setRawResponse(data);

        // Handle the response structure - result.result contains the product data
        const productData = data.result?.result || data.product || data.data || data;
        setProductDetails(productData);

        // Extract variants/SKUs - try multiple possible paths
        let skuInfos = [];

        // Try different paths for SKU data
        if (productData.productSkuInfos?.length > 0) {
          skuInfos = productData.productSkuInfos;
        } else if (productData.skuInfos?.length > 0) {
          skuInfos = productData.skuInfos;
        } else if (productData.skuList?.length > 0) {
          skuInfos = productData.skuList;
        } else if (productData.skus?.length > 0) {
          skuInfos = productData.skus;
        } else if (productData.productInfo?.skuInfos?.length > 0) {
          skuInfos = productData.productInfo.skuInfos;
        } else if (productData.data?.productSkuInfos?.length > 0) {
          skuInfos = productData.data.productSkuInfos;
        } else if (Array.isArray(productData.variants)) {
          skuInfos = productData.variants;
        }

        console.log('Found SKU infos:', skuInfos);

        if (skuInfos.length > 0) {
          const processedVariants = await Promise.all(
            skuInfos.map(async (sku, idx) => {
              // Try different price field names
              const skuPrice = parseFloat(
                sku.price ||
                sku.consignPrice ||
                sku.salePrice ||
                sku.retailPrice ||
                sku.priceInfo?.price ||
                rmb
              );
              const skuInr = await calculateINRPrice(skuPrice);

              // Get attributes - try different structures
              let attributes = [];
              let attrImageUrl = null;
              if (sku.skuAttributes?.length > 0) {
                attributes = sku.skuAttributes.map(attr => {
                  // Check for image URL in attribute
                  if (!attrImageUrl && attr.skuImageUrl) {
                    attrImageUrl = attr.skuImageUrl;
                  }
                  return {
                    name: attr.attributeNameTrans || attr.attributeName || attr.name || 'Attribute',
                    value: attr.valueTrans || attr.value || attr.attributeValue || '',
                    imageUrl: attr.skuImageUrl || null
                  };
                });
              } else if (sku.attributes?.length > 0) {
                attributes = sku.attributes.map(attr => {
                  if (!attrImageUrl && (attr.skuImageUrl || attr.imageUrl)) {
                    attrImageUrl = attr.skuImageUrl || attr.imageUrl;
                  }
                  return {
                    name: attr.name || attr.attributeName || 'Attribute',
                    value: attr.value || attr.attributeValue || '',
                    imageUrl: attr.skuImageUrl || attr.imageUrl || null
                  };
                });
              } else if (sku.specAttrs?.length > 0) {
                attributes = sku.specAttrs.map(attr => ({
                  name: attr.name || 'Attribute',
                  value: attr.value || ''
                }));
              }

              // Get SKU image - try different field names including attribute images
              const skuImage = attrImageUrl || sku.skuImage || sku.image || sku.imageUrl || sku.pic || sku.picUrl || image;

              // Get SKU ID - try different field names
              const skuId = sku.skuId || sku.id || sku.specId || `sku-${idx}`;

              return {
                id: skuId,
                skuId: skuId,
                image: skuImage,
                price: { rmb: skuPrice, inr: skuInr },
                stock: sku.amountOnSale || sku.stock || sku.canBookCount || sku.quantity || 0,
                attributes: attributes,
                attributeText: attributes.map(a => a.value).filter(v => v).join(' / ') || sku.specAttrsText || `Variant ${idx + 1}`
              };
            })
          );
          console.log('Processed variants:', processedVariants);
          setVariants(processedVariants);
        } else {
          console.log('No SKU infos found in response');
        }
      } catch (err) {
        console.error('Failed to fetch variants:', err);
        setApiError(err.message);
        message.showToast('Failed to load product variants', ToastTypes.ERROR);
      } finally {
        setLoading(false);
      }
    };

    fetchVariants();
  }, [offerId, rmb, image, calculateINRPrice, message]);

  // Map variant to ASIN
  const handleMapVariant = async (variant) => {
    if (!asin || !offerId) {
      message.showToast('Missing ASIN or Product ID', ToastTypes.ERROR);
      return;
    }

    setMappingSkuId(variant.skuId);

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      if (!token) {
        message.showToast('Please login to map products', ToastTypes.ERROR);
        return;
      }

      const response = await fetch(`${BACKEND_API}admin/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: offerId,
          skuId: variant.skuId,
          productTitle: title,
          productImage: variant.image || image,
          productPrice: variant.price.rmb,
          variantAttributes: variant.attributeText,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.showToast(`Successfully mapped ASIN ${asin} to SKU ${variant.skuId}`, ToastTypes.SUCCESS);
      } else {
        message.showToast(data.message || 'Failed to map variant', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Map variant failed:', error);
      message.showToast('Failed to map variant', ToastTypes.ERROR);
    } finally {
      setMappingSkuId(null);
    }
  };

  // Map main product (no specific variant)
  const handleMapProduct = async () => {
    if (!asin || !offerId) {
      message.showToast('Missing ASIN or Product ID', ToastTypes.ERROR);
      return;
    }

    setMappingSkuId('main');

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      if (!token) {
        message.showToast('Please login to map products', ToastTypes.ERROR);
        return;
      }

      const response = await fetch(`${BACKEND_API}admin/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: offerId,
          skuId: null,
          productTitle: title,
          productImage: image,
          productPrice: rmb,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        message.showToast(`Successfully mapped ASIN ${asin} to product ${offerId}`, ToastTypes.SUCCESS);
      } else {
        message.showToast(data.message || 'Failed to map product', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Map product failed:', error);
      message.showToast('Failed to map product', ToastTypes.ERROR);
    } finally {
      setMappingSkuId(null);
    }
  };

  const open1688 = () => {
    window.open(`https://detail.1688.com/offer/${offerId}.html`, '_blank');
  };

  return (
    <div className="pv-container">
      {/* Header */}
      <div className="pv-header">
        <button className="pv-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="pv-title">Product Variants</h2>
        <button className="pv-1688-btn" onClick={open1688}>
          <FaExternalLinkAlt size={12} /> View on 1688
        </button>
      </div>

      {/* Product Info Card */}
      <div className="pv-product-card">
        <div className="pv-product-image">
          <img src={image || noImage} alt={title} onError={(e) => { e.currentTarget.src = noImage; }} />
        </div>
        <div className="pv-product-info">
          <h3 className="pv-product-title">{title}</h3>
          <div className="pv-product-meta">
            <span className="pv-asin">ASIN: {asin}</span>
            <span className="pv-offerid">Offer ID: {offerId}</span>
          </div>
          <div className="pv-product-prices">
            <span className="pv-price-rmb">¥{rmb.toFixed(2)}</span>
            <span className="pv-price-inr">₹{inr.toFixed(2)}</span>
          </div>
          <button
            className="pv-map-main-btn"
            onClick={handleMapProduct}
            disabled={mappingSkuId === 'main'}
          >
            {mappingSkuId === 'main' ? <FaSpinner className="pv-spinner" /> : <FaCheck />}
            Map Main Product to ASIN
          </button>
        </div>
      </div>

      {/* Variants Section */}
      <div className="pv-variants-section">
        <h3 className="pv-section-title">
          Available Variants ({variants.length})
        </h3>

        {loading ? (
          <div className="pv-loading">
            <FaSpinner className="pv-spinner" />
            <p>Loading variants...</p>
          </div>
        ) : variants.length > 0 ? (
          <div className="pv-variants-grid">
            {variants.map((variant) => (
              <div key={variant.id} className="pv-variant-card">
                <div className="pv-variant-image">
                  <img
                    src={variant.image || image || noImage}
                    alt={variant.attributeText}
                    onError={(e) => { e.currentTarget.src = noImage; }}
                  />
                </div>
                <div className="pv-variant-info">
                  <div className="pv-variant-sku">SKU: {variant.skuId}</div>
                  <div className="pv-variant-attrs">{variant.attributeText}</div>
                  <div className="pv-variant-prices">
                    <span className="pv-variant-rmb">¥{variant.price.rmb.toFixed(2)}</span>
                    <span className="pv-variant-inr">₹{variant.price.inr.toFixed(2)}</span>
                  </div>
                  {variant.stock > 0 && (
                    <div className="pv-variant-stock">Stock: {variant.stock}</div>
                  )}
                </div>
                <button
                  className="pv-variant-map-btn"
                  onClick={() => handleMapVariant(variant)}
                  disabled={mappingSkuId === variant.skuId}
                >
                  {mappingSkuId === variant.skuId ? '...' : 'Map'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="pv-no-variants">
            <p>No variants available for this product.</p>
            <p>You can map the main product directly using the button above.</p>
            {apiError && (
              <p className="pv-error-text">Error: {apiError}</p>
            )}
            {rawResponse && (
              <details className="pv-debug-details">
                <summary>Debug: API Response</summary>
                <pre className="pv-debug-pre">{JSON.stringify(rawResponse, null, 2)}</pre>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
